<template>
  <div class="visa-container">
    <img src="/images/hri_dashboard/WPVISA-IMPORTANT-NOTICE.png" alt="VISA & Work Permit Important Notice" class="visa-image" />
    
    <a-list class="performance-list mt-20" item-layout="vertical" size="large">
      <a-list-item>
        <a-list-item-meta>
          <div slot="description" class="font-web">
            The checklist outlines the documents required for processing your Visa and Work Permit extension.
          </div>
          <div slot="title" class="font-web performance-item-title-wrapper">
            <span class="performance-item-title">1. VISA & Work Permit Extension</span>
          </div>
        </a-list-item-meta>

        <div class="performance-item-content">
          <!-- 1.1 Documents for Visa Extension -->
          <div class="content-section mb-10 mt-15">
            <h4 class="font-web section-label">1.1 Documents for Visa Extension</h4>
          </div>
          <a-spin :spinning="loading">
            <table class="doc-table">
              <thead>
                <tr>
                  <th>รายการไฟล์ / File List</th>
                  <th class="text-center">Download</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(doc, idx) in visaDocs" :key="'visa-'+idx">
                  <td>
                    <div class="file-name-main">{{ doc.thName }}</div>
                    <div v-if="doc.enName" class="file-name-sub">({{ doc.enName }})</div>
                    <div v-if="doc.updatedAt" class="file-date">อัปเดตเมื่อ: {{ formatDate(doc.updatedAt) }}</div>
                  </td>
                  <td class="text-center">
                    <a v-if="doc.thLink" :href="doc.thLink" target="_blank" class="download-link">
                      <a-icon type="download" />
                    </a>
                    <span v-else>-</span>
                  </td>
                </tr>
                <tr v-if="!visaDocs || visaDocs.length === 0">
                  <td colspan="2" class="text-center" style="color: #999;">No documents found</td>
                </tr>
              </tbody>
            </table>
          </a-spin>

          <!-- 1.2 Documents for Work Permit Extension -->
          <div class="content-section mb-10 mt-20">
            <h4 class="font-web section-label">1.2 Documents for Work Permit Extension</h4>
          </div>
          <a-spin :spinning="loading">
            <table class="doc-table">
              <thead>
                <tr>
                  <th>รายการไฟล์ / File List</th>
                  <th class="text-center">Download</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(doc, idx) in wpDocs" :key="'wp-'+idx">
                  <td>
                    <div class="file-name-main">{{ doc.thName }}</div>
                    <div v-if="doc.enName" class="file-name-sub">({{ doc.enName }})</div>
                    <div v-if="doc.updatedAt" class="file-date">อัปเดตเมื่อ: {{ formatDate(doc.updatedAt) }}</div>
                  </td>
                  <td class="text-center">
                    <a v-if="doc.thLink" :href="doc.thLink" target="_blank" class="download-link">
                      <a-icon type="download" />
                    </a>
                    <span v-else>-</span>
                  </td>
                </tr>
                <tr v-if="!wpDocs || wpDocs.length === 0">
                  <td colspan="2" class="text-center" style="color: #999;">No documents found</td>
                </tr>
              </tbody>
            </table>
          </a-spin>

        </div>
        
        <template slot="extra">
          <div style="display: flex; flex-direction: column; gap: 15px;">
            <img 
              width="272" 
              alt="Checklist for Visa Page 1" 
              src="/images/hri_dashboard/Page1_ChecklistforVisa.png" 
              class="clickable-img performance-item-img" 
              @click="handlePreview('/images/hri_dashboard/Page1_ChecklistforVisa.png')" 
            />
            <img 
              width="272" 
              alt="Checklist for Visa Page 2" 
              src="/images/hri_dashboard/Page2_ChecklistforVisa.png" 
              class="clickable-img performance-item-img" 
              @click="handlePreview('/images/hri_dashboard/Page2_ChecklistforVisa.png')" 
            />
          </div>
        </template>
      </a-list-item>
    </a-list>

    <!-- Image Preview Modal -->
    <a-modal :visible="previewVisible" :footer="null" @cancel="handleCancelPreview" width="80%">
      <img alt="preview" style="width: 100%" :src="previewImage" />
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    return {
      loading: false,
      docs: [],
      visaDocs: [],
      wpDocs: [],
      previewVisible: false,
      previewImage: ''
    };
  },
  mounted() {
    this.fetchDocs();
  },
  methods: {
    async fetchDocs() {
      this.loading = true;
      try {
        const res = await this.$http.get('/hri/doc-file/?doc_type=form');
        if (res.status === 200 && Array.isArray(res.data)) {
          this.docs = res.data;
          
          // 1.1 Visa Extension (_DV_)
          const dvDocs = this.docs.filter(d => d.file_name && d.file_name.includes('_DV_'));
          dvDocs.sort((a, b) => {
            const isEnA = a.file_name.includes('_DV_en_');
            const isEnB = b.file_name.includes('_DV_en_');
            
            if (isEnA && !isEnB) return -1;
            if (!isEnA && isEnB) return 1;

            const getNum = (name) => {
              const match = name.match(/_DV_(?:en_)?(\d+)/);
              return match ? parseInt(match[1], 10) : 0;
            };

            const numA = getNum(a.file_name);
            const numB = getNum(b.file_name);
            
            if (numA !== numB) return numA - numB;
            return a.file_name.localeCompare(b.file_name);
          });

          this.visaDocs = dvDocs.map(doc => ({
            thName: doc.file_name.replace(/_DV.*$/, ''),
            thLink: doc.doc_file,
            updatedAt: doc.updated_at || doc.created_at
          }));

          // 1.2 Work Permit Extension (_DW_)
          const wpDocs = this.docs.filter(d => d.file_name && d.file_name.includes('_DW_'));
          wpDocs.sort((a, b) => {
            const isEnA = a.file_name.includes('_DW_en_');
            const isEnB = b.file_name.includes('_DW_en_');
            
            if (isEnA && !isEnB) return -1;
            if (!isEnA && isEnB) return 1;

            const getNum = (name) => {
              const match = name.match(/_DW_(?:en_)?(\d+)/);
              return match ? parseInt(match[1], 10) : 0;
            };

            const numA = getNum(a.file_name);
            const numB = getNum(b.file_name);
            
            if (numA !== numB) return numA - numB;
            return a.file_name.localeCompare(b.file_name);
          });
          
          this.wpDocs = wpDocs.map(doc => ({
            thName: doc.file_name.replace(/_DW.*$/, ''),
            thLink: doc.doc_file,
            updatedAt: doc.updated_at || doc.created_at
          }));
        }
      } catch (error) {
        console.error("Error fetching docs:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD/MM/YYYY HH:mm');
    },
    handlePreview(image) {
      if (image) {
        this.previewImage = image;
        this.previewVisible = true;
      }
    },
    handleCancelPreview() {
      this.previewVisible = false;
    }
  }
};
</script>

<style scoped>
.visa-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  width: 100%;
  margin-bottom: 80px;
}

.visa-image {
  max-width: 65%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
}

@media (max-width: 768px) {
  .visa-image {
    max-width: 90%;
  }
}

.performance-list {
  width: 100%;
  padding: 0 20px;
}

.performance-item-title {
  font-size: 20px !important;
  font-weight: 800 !important;
  color: #1a1a2e !important;
}

.performance-item-content {
  color: #595959;
  font-size: 14px;
  line-height: 1.6;
}

.section-label {
  font-size: 13px;
  font-weight: 700;
  color: #8c8c8c;
  margin-bottom: 5px;
  letter-spacing: 0.5px;
}

.performance-item-title-wrapper {
  margin-bottom: 4px;
}

.performance-item-img {
  width: 280px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  object-fit: cover;
  transition: transform 0.3s ease;
}

.clickable-img {
  cursor: pointer;
}

.clickable-img:hover {
  transform: scale(1.05) !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Document Table Styles */
.doc-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.doc-table th {
  background: #1a1a2e;
  padding: 12px 15px;
  text-align: left;
  font-weight: 700;
  color: #ffffff;
  border-bottom: 2px solid #4285F4;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.doc-table td {
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
  color: #434343;
}

.doc-table tr:hover {
  background-color: #fdfdfd;
}

.doc-table tr:last-child td {
  border-bottom: none;
}

.file-name-main {
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.4;
}

.file-name-sub {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 2px;
  font-style: italic;
}

.file-date {
  font-size: 11px;
  color: #bfbfbf;
  margin-top: 4px;
}

.text-center {
  text-align: center;
}

.download-link {
  color: #4285F4;
  font-size: 18px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.download-link:hover {
  color: #1a73e8;
  transform: translateY(-2px);
}

.mt-15 {
  margin-top: 15px;
}

.mt-20 {
  margin-top: 20px;
}

.mb-10 {
  margin-bottom: 10px;
}

@media (max-width: 768px) {
  .performance-item-img {
    width: 100% !important;
    max-width: 320px;
    margin-top: 10px;
  }

  ::v-deep .ant-list-item-extra {
    margin-left: 0 !important;
    display: flex;
    justify-content: center;
  }
}
</style>
